import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTeam } from "../../context/TeamContext";
import { api } from "../../api/clientInstance";
import { Copy, Check, Loader, Trash2 } from "lucide-react";
import "./CupPage.css";

// Pick a champion line based on the points gap over the runner-up — a narrow
// win vs a thrashing get their own (cheekier) message.
const championKey = (margin) =>
  margin == null ? "cup.champion" :
  margin <= 2    ? "cup.championNarrow" :
  margin >= 6    ? "cup.championThrash" :
                   "cup.champion";

// Minimum members required to start a cup (mirror of backend MIN_CUP_MEMBERS).
const MIN_CUP_MEMBERS = 4;

// ── Shared header ─────────────────────────────────────────────────────────────

function CupHeader({ cup, onCopy, copied }) {
  const { t } = useTranslation();
  return (
    <div className="cup-header">
      <h2 className="cup-title">{cup.name}</h2>
      <span className={`cup-status-badge cup-status-badge--${cup.status}`}>
        {t(`cup.status.${cup.status}`)}
      </span>
      {cup.join_code && (
        <div className="cup-code-row">
          <span className="cup-code">{cup.join_code}</span>
          <button className="cup-copy-btn" onClick={() => onCopy(cup.join_code)}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Active phase: members + readiness + start ─────────────────────────────────

function ActiveView({ cup, isOrganizer, onStarted, onMemberRemoved, onDeleted }) {
  const { t }  = useTranslation();
  const [members,  setMembers]  = useState([]);
  const [starting, setStarting] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState("");
  const [error,    setError]    = useState(null);

  const isOpen = cup.type === "school";

  const loadMembers = useCallback(() => {
    api(`/cup/${cup.id}/members`).then(setMembers).catch(() => {});
  }, [cup.id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    try {
      await api(`/cup/${cup.id}/start`, { method: "POST" });
      onStarted();
    } catch (e) {
      // Backend returns {not_ready: [...]} inside the detail field
      const detail = e.detail ?? e.message;
      setError(typeof detail === "object" ? detail : e.message);
    } finally {
      setStarting(false);
    }
  };

  const handleRemove = async (userId) => {
    setRemoving(userId);
    try {
      await api(`/cup/${cup.id}/member/${userId}`, { method: "DELETE" });
      setMembers((prev) => prev.filter((m) => m.id !== userId));
      onMemberRemoved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setRemoving(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api(`/cup/${cup.id}`, { method: "DELETE" });
      onDeleted?.();
    } catch (e) {
      setError(e.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleReset = async () => {
    if (!resetTarget || !newPassword) return;
    setResetting(true);
    setResetError("");
    try {
      await api(`/cup/${cup.id}/member/${resetTarget.id}/reset-password`, {
        method: "PATCH",
        body: JSON.stringify({ new_password: newPassword }),
      });
      setResetTarget(null);
      setNewPassword("");
    } catch (e) {
      setResetError(e.message);
    } finally {
      setResetting(false);
    }
  };

  const enoughMembers = members.length >= MIN_CUP_MEMBERS;
  const allReady = enoughMembers && members.every((m) => m.ready);
  const startHint = !enoughMembers
    ? t("cup.startNeedMembers", { count: MIN_CUP_MEMBERS })
    : t("cup.startDisabledHint");

  return (
    <div className="cup-section">
      <p className="cup-section-label">{t("cup.members")} ({members.length})</p>
      {members.map((m) => (
        <div key={m.id} className="cup-member-row">
          {isOpen
            ? <span className={`cup-ready-dot cup-ready-dot--${m.ready ? "yes" : "no"}`} title={m.reason ?? ""} />
            : <span className={`cup-mstatus cup-mstatus--${m.status}`}>{t(`cup.mstatus.${m.status}`)}</span>}
          <span className="cup-member-name">{m.name}</span>
          {isOrganizer && !m.has_email && (
            <button
              className="cup-reset-btn"
              onClick={() => { setResetTarget(m); setNewPassword(""); setResetError(""); }}
            >
              {t("cup.resetPassword")}
            </button>
          )}
          {isOrganizer && m.id !== cup.owner_id && (
            <button
              className="cup-remove-btn"
              onClick={() => handleRemove(m.id)}
              disabled={removing === m.id}
              title={t("cup.removeMember")}
            >
              {removing === m.id ? <Loader size={12} className="spin" /> : "✕"}
            </button>
          )}
        </div>
      ))}

      {resetTarget && (
        <div className="cup-reset-form">
          <p className="cup-section-label">{t("cup.resetFor", { name: resetTarget.name })}</p>
          <div className="country-picker">
            <input
              className="country-select"
              type="password"
              placeholder={t("cup.newPassword")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={resetting}
            />
            <button className="team-save-btn" onClick={handleReset} disabled={!newPassword || resetting}>
              {resetting ? <Loader size={16} className="spin" /> : "✓"}
            </button>
          </div>
          {resetError && <p className="cup-error">{resetError}</p>}
        </div>
      )}

      {!isOpen && cup.status === "pending" && (
        <p className="cup-section-hint">{t("cup.friendPendingNote")}</p>
      )}

      {isOrganizer && isOpen && (
        <button
          className="btn-primary cup-start-btn"
          onClick={handleStart}
          disabled={!allReady || starting}
          title={!allReady ? startHint : undefined}
        >
          {starting ? <Loader size={14} className="spin" /> : t("cup.start")}
        </button>
      )}

      {isOrganizer && (
        !confirmDelete ? (
          <button className="cup-delete-btn" onClick={() => setConfirmDelete(true)} disabled={deleting}>
            {t("cup.delete")}
          </button>
        ) : (
          <div className="cup-confirm-row">
            <span className="cup-confirm-label">{t("cup.deleteConfirm")}</span>
            <button className="cup-delete-btn" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader size={14} className="spin" /> : t("cup.deleteConfirmYes")}
            </button>
            <button className="cup-cancel-btn" onClick={() => setConfirmDelete(false)} disabled={deleting}>
              {t("cancel")}
            </button>
          </div>
        )
      )}

      {error && (
        <div className="cup-error">
          {typeof error === "object" && error.not_ready
            ? error.not_ready.map((u) => (
                <p key={u.user_id}>{u.name}: {t(`cup.reason.${u.reason}`)}</p>
              ))
            : <p>{error}</p>
          }
        </div>
      )}
    </div>
  );
}

// ── Started / Ended phase: standings + fixtures ───────────────────────────────

function StandingsTable({ cupId, refreshKey }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api(`/cup/${cupId}/standings`).then(setRows).catch(() => {});
  }, [cupId, refreshKey]);

  if (!rows.length) return null;

  return (
    <div className="cup-section">
      <p className="cup-section-label">{t("cup.standings")}</p>
      <table className="cup-standings-table">
        <thead>
          <tr>
            <th>#</th>
            <th>{t("cup.team")}</th>
            <th title={t("cup.played")}>P</th>
            <th title={t("wins")}>W</th>
            <th title={t("draws")}>D</th>
            <th title={t("losses")}>L</th>
            <th title={t("cup.goalDiff")}>GD</th>
            <th title={t("cup.points")}>Pts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.team_id}>
              <td className="cup-rank">{i + 1}</td>
              <td className="cup-team-name">
                {r.team}
                {r.manager && <span className="coach-sub">{r.manager}</span>}
              </td>
              <td>{r.played}</td>
              <td>{r.wins}</td>
              <td>{r.draws}</td>
              <td>{r.losses}</td>
              <td>{r.goal_diff > 0 ? `+${r.goal_diff}` : r.goal_diff}</td>
              <td className="cup-pts">{r.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FixturesList({ cup, myTeam, isOrganizer, onPlayed }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fixtures, setFixtures] = useState([]);
  const [playing,  setPlaying]  = useState(null);
  const [error,    setError]    = useState(null);

  const loadFixtures = useCallback(() => {
    api(`/cup/${cup.id}/fixtures`).then(setFixtures).catch(() => {});
  }, [cup.id]);

  useEffect(() => { loadFixtures(); }, [loadFixtures]);

  const handlePlay = async (fixtureId) => {
    setPlaying(fixtureId);
    setError(null);
    try {
      const result = await api(`/game/cup/${fixtureId}`, { method: "POST" });
      onPlayed?.();
      loadFixtures();
      navigate(`/match/${result.match_id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setPlaying(null);
    }
  };

  if (!fixtures.length) return null;

  return (
    <div className="cup-section">
      <p className="cup-section-label">{t("cup.fixtures")}</p>
      {fixtures.map((f) => {
        const isMyFixture = myTeam && (f.home_team_id === myTeam.id || f.away_team_id === myTeam.id);
        return (
          <div key={f.fixture_id} className="cup-fixture-row">
            <span className="cup-fixture-teams">
              <span className="cup-fixture-team">
                <span>{f.home_team}</span>
                {f.home_manager && <span className="coach-sub">{f.home_manager}</span>}
              </span>
              <span className="cup-fixture-sep">–</span>
              <span className="cup-fixture-team">
                <span>{f.away_team}</span>
                {f.away_manager && <span className="coach-sub">{f.away_manager}</span>}
              </span>
            </span>
            {f.status === "played" ? (
              <>
                <span className="cup-fixture-score">{f.home_score}–{f.away_score}</span>
                {f.match_id && (
                  <button className="cup-watch-btn" onClick={() => navigate(`/match/${f.match_id}`)}>
                    {t("dashboard.watch")}
                  </button>
                )}
              </>
            ) : (
              (isMyFixture || isOrganizer) && cup.status === "started" && (
                <button
                  className="btn-primary cup-play-btn"
                  onClick={() => handlePlay(f.fixture_id)}
                  disabled={playing === f.fixture_id}
                >
                  {playing === f.fixture_id ? "…" : t("dashboard.play")}
                </button>
              )
            )}
          </div>
        );
      })}
      {error && <p className="cup-error">{error}</p>}
    </div>
  );
}

// ── Ended phase: close button ─────────────────────────────────────────────────

function RemoveSection({ cup, onRemoved }) {
  const { t } = useTranslation();
  const [confirm,  setConfirm]  = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error,    setError]    = useState(null);

  const handleRemove = async () => {
    setRemoving(true);
    setError(null);
    try {
      await api(`/cup/${cup.id}/remove`, { method: "POST" });
      onRemoved();
    } catch (e) {
      setError(e.message);
      setRemoving(false);
      setConfirm(false);
    }
  };

  return (
    <div className="cup-section cup-close-section">
      {!confirm ? (
        <button className="cup-remove-icon-btn" onClick={() => setConfirm(true)} title={t("cup.remove")} aria-label={t("cup.remove")}>
          <Trash2 size={18} />
        </button>
      ) : (
        <div className="cup-confirm-row">
          <span className="cup-confirm-label">{t("cup.removeConfirm")}</span>
          <button className="cup-close-btn" onClick={handleRemove} disabled={removing}>
            {removing ? <Loader size={14} className="spin" /> : t("cup.removeConfirmYes")}
          </button>
          <button className="cup-cancel-btn" onClick={() => setConfirm(false)} disabled={removing}>{t("cancel")}</button>
        </div>
      )}
      {error && <p className="cup-error">{error}</p>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CupPage() {
  const { t }       = useTranslation();
  const { cupId }   = useParams();
  const navigate    = useNavigate();
  const { team }    = useTeam();
  const [cup,     setCup]     = useState(null);
  const [copied,  setCopied]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [playedTick, setPlayedTick] = useState(0);

  const loadCup = useCallback(() => {
    api(`/cup/${cupId}`)
      .then(setCup)
      .catch(() => setCup(null))
      .finally(() => setLoading(false));
  }, [cupId]);

  useEffect(() => { loadCup(); }, [loadCup]);

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="cup-page-loading">…</div>;
  if (!cup)    return <div className="cup-page-loading">Cup not found</div>;

  const isOrganizer = cup.is_organizer;

  return (
    <div className="cup-page">
      <CupHeader cup={cup} onCopy={handleCopy} copied={copied} />

      {(cup.status === "ended" || cup.status === "closed") && (
        cup.winner
          ? <div className="cup-champion">🏆 {t(championKey(cup.winner.margin), { name: cup.winner.name })}</div>
          : <div className="cup-champion cup-champion--none">{t("cup.noChampion")}</div>
      )}

      {(cup.status === "active" || cup.status === "pending") && (
        <ActiveView
          cup={cup}
          isOrganizer={isOrganizer}
          onStarted={loadCup}
          onMemberRemoved={loadCup}
          onDeleted={() => navigate("/cups")}
        />
      )}

      {(cup.status === "started" || cup.status === "ended") && (
        <>
          <StandingsTable cupId={cup.id} refreshKey={playedTick} />
          <FixturesList
            cup={cup}
            myTeam={team}
            isOrganizer={isOrganizer}
            onPlayed={() => { setPlayedTick((n) => n + 1); loadCup(); }}
          />
        </>
      )}

      {cup.status === "ended" && isOrganizer && (
        <RemoveSection cup={cup} onRemoved={() => navigate("/cups")} />
      )}

      {cup.status === "closed" && (
        <StandingsTable cupId={cup.id} />
      )}
    </div>
  );
}
